"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import React, {
  useCallback,
  useEffect,
  useState,
  createContext,
  useContext
} from "react";
import { Job, JobProfile, Facility } from "@/features/settings/types";

interface JobsManagementContextType {
  jobs: Job[];
  jobProfiles: JobProfile[];
  loading: boolean;
  isJobModalOpen: boolean;
  selectedJob: Job | undefined;
  companyId: string | null;
  facilities: Facility[];
  fetchJobs: (companyId: string) => Promise<void>;
  setIsJobModalOpen: (open: boolean) => void;
  setSelectedJob: (job: Job | undefined) => void;
}

const JobsManagementContext = createContext<
  JobsManagementContextType | undefined
>(undefined);

export function JobsManagementProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobProfiles, setJobProfiles] = useState<JobProfile[]>([]);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | undefined>();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchJobProfiles = useCallback(
    async (companyId: string) => {
      try {
        let profilesData, profilesError;
        if (!profile!.isSuperUser) {
          const { data, error } = await supabase
            .from("job_profiles")
            .select("*")
            .eq("company_id", companyId);
          profilesData = data;
          profilesError = error;
        } else {
          const { data, error } = await supabase
            .from("job_profiles")
            .select("*");

          profilesData = data;
          profilesError = error;
        }

        if (profilesError) throw profilesError;
        setJobProfiles(profilesData || []);
      } catch (error) {
        console.error("Error fetching job profiles:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Stellenprofile",
          variant: "destructive"
        });
      }
    },
    [toast, profile]
  );

  const fetchJobs = useCallback(
    async (companyId: string) => {
      try {
        setLoading(true);
        let jobsData, jobsError;
        if (!profile!.isSuperUser) {
          const { data, error } = await supabase
            .from("jobs")
            .select("*")
            .eq("company_id", companyId)
            .order("created_at", { ascending: false });
          jobsData = data;
          jobsError = error;
        } else {
          const { data, error } = await supabase
            .from("jobs")
            .select("*")
            .order("created_at", { ascending: false });

          jobsData = data;
          jobsError = error;
        }

        if (jobsError) throw jobsError;
        setJobs(jobsData || []);
      } catch (error) {
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Stellenangebote",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    },
    [toast, profile]
  );

  const fetchFacilities = useCallback(
    async (companyId: string) => {
      try {
        const { data: facilitiesData, error: facilitiesError } = await supabase
          .from("facilities")
          .select("*")
          .eq("company_id", companyId);

        if (facilitiesError) throw facilitiesError;
        setFacilities(facilitiesData || []);
      } catch (error) {
        console.error("Error fetching facilities:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Einrichtungen",
          variant: "destructive"
        });
      }
    },
    [toast]
  );

  const fetchCompanyId = useCallback(async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("employer_profiles")
        .select("company_id")
        .eq("id", profile.id)
        .single();
      if (profileError) throw profileError;
      setCompanyId(profileData?.company_id);
    } catch (error) {
      console.error("Error fetching company ID:", error);
      setCompanyId(null);
      toast({
        title: "Fehler",
        description: "Fehler beim Laden der Unternehmensdaten",
        variant: "destructive"
      });
    }
  }, [profile.id, toast]);

  useEffect(() => {
    if (!profile.anonymous) {
      fetchCompanyId();
    }
  }, [profile, fetchCompanyId]);

  useEffect(() => {
    if (!profile.anonymous && companyId) {
      Promise.all([
        fetchJobs(companyId),
        fetchJobProfiles(companyId),
        fetchFacilities(companyId)
      ]);
    }
  }, [profile, companyId, fetchJobs, fetchJobProfiles, fetchFacilities]);

  return (
    <JobsManagementContext.Provider
      value={{
        jobs,
        jobProfiles,
        loading,
        isJobModalOpen,
        selectedJob,
        companyId,
        facilities,
        fetchJobs,
        setIsJobModalOpen,
        setSelectedJob
      }}
    >
      {children}
    </JobsManagementContext.Provider>
  );
}

export function useJobsManagement() {
  const context = useContext(JobsManagementContext);
  if (context === undefined) {
    throw new Error(
      "useJobsManagement must be used within a JobsManagementProvider"
    );
  }
  return context;
}
